// Toplu İçe Aktarım Dialog — Excel/CSV yükleme, preview, doğrulama, gönderim
// Her modülün ListPage'inde "İçe Aktar" butonu bu bileşeni açar.
import { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { Upload, Download, X, AlertCircle, CheckCircle2, FileUp, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/api/client";
import { toast } from "sonner";

// module = { slug, label } — hangi modül için import açıldığı
export function ImportDialog({ moduleSlug, moduleLabel, onClose, onImported }) {
  const [spec, setSpec] = useState(null);          // { columns: [...] }
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);            // parsed rows preview
  const [parseError, setParseError] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get(`/import/modules`)
      .then(({ data }) => {
        const m = data.find(x => x.slug === moduleSlug);
        setSpec(m);
      })
      .catch(e => toast.error(formatApiError(e)));
  }, [moduleSlug]);

  const downloadTemplate = async () => {
    try {
      const resp = await api.get(`/import/${moduleSlug}/template`, { responseType: "blob" });
      const blob = new Blob([resp.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sablon_${moduleSlug}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Şablon indirildi");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setParseError("");
    setRows([]);
    setResult(null);

    try {
      const buf = await f.arrayBuffer();
      // ============================================================
      // TIMEZONE-SAFE EXCEL DATE OKUMA
      // ============================================================
      // XLSX.js'nin `cellDates:true` opsiyonu, base'i `new Date(1899, 11, 30)` (LOCAL)
      // olarak kullanır. Bu Türkiye timezone'unda (UTC+3) Date objelerini bir gün
      // erken UTC noktasında oluşturur — 31.07 → getUTCDate()=30 bug'ı bundan gelir.
      //
      // ÇÖZÜM: cellDates:FALSE ile oku. Tarih hücrelerinde Excel ham serial number
      // dönüyor (cell.t === 'n' + cell.z bir date format). Bu ham sayıyı bize UTC
      // bazlı formülle çeviriyoruz. Locale/timezone TAMAMEN devre dışı.
      // ============================================================
      const wb = XLSX.read(buf, {
        type: "array",
        cellDates: false,  // ← KRİTİK: Date object oluşturma
        cellNF: true,      // Cell number format'ı sakla (tarih tespiti için)
      });
      const sheetName = wb.SheetNames.find(n => n.toLowerCase() !== "talimat")
                        || wb.SheetNames[0];
      const ws = wb.Sheets[sheetName];
      if (!ws || !ws["!ref"]) {
        setParseError("Dosya boş görünüyor.");
        return;
      }

      const range = XLSX.utils.decode_range(ws["!ref"]);

      // Bir cell'in tarih formatlı olduğunu tespit et — format string'de d/m/y varsa
      const isDateFormat = (fmt) => {
        if (!fmt) return false;
        // Excel tarih formatları: dd/mm/yyyy, m/d/yy, d.m.yyyy, yyyy-mm-dd, vs.
        // "d", "m" veya "y" içeriyorsa ve number format değilse
        const clean = String(fmt).replace(/\[.*?\]/g, "").toLowerCase();
        return /(\bd+\b|\bm+\b|\by+\b)/.test(clean) &&
               !/^(#|0|general)/i.test(clean);
      };

      // Excel serial → DD.MM.YYYY (Türkçe format, TIMEZONE-BAĞIMSIZ)
      const serialToDDMMYYYY = (serial) => {
        // Excel epoch: 1900-01-01 = serial 1. 1900 leap bug (serial 60 = 29 Şubat hayali)
        // Base 1899-12-30 UTC → serial 1'de + 1 gün = 1900-01-01 doğru
        const utcMs = Date.UTC(1899, 11, 30) + serial * 86400000;
        const dt = new Date(utcMs);
        const dd = String(dt.getUTCDate()).padStart(2, "0");
        const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
        const yy = dt.getUTCFullYear();
        return `${dd}.${mm}.${yy}`;
      };

      // Header row (row 0'da)
      const headerRow = [];
      for (let c = range.s.c; c <= range.e.c; c++) {
        const cell = ws[XLSX.utils.encode_cell({ r: range.s.r, c })];
        headerRow.push(cell ? String(cell.v || "").replace(" *", "").trim() : "");
      }

      const cols = spec?.columns || [];
      const idxToKey = headerRow.map(h => {
        const found = cols.find(c => c.header.replace(" *", "").trim() === h);
        return found ? found.key : null;
      });

      // Hangi kolon key'i tarih olarak işlenecek
      const DATE_KEYS = new Set([
        "due_date", "issue_date", "next_run", "date",
        "payment_date", "statement_date",
      ]);

      const parsed = [];
      // Veri satırları (row 1'den itibaren)
      for (let r = range.s.r + 1; r <= range.e.r; r++) {
        const obj = {};
        let hasAny = false;
        let isHintRow = false;

        for (let cIdx = 0; cIdx < idxToKey.length; cIdx++) {
          const key = idxToKey[cIdx];
          if (!key) continue;
          const c = range.s.c + cIdx;
          const cell = ws[XLSX.utils.encode_cell({ r, c })];
          if (!cell) { obj[key] = ""; continue; }

          let val = "";

          if (DATE_KEYS.has(key)) {
            // TARİH kolonu — özel işlem
            if (cell.t === "n" && typeof cell.v === "number") {
              // Sayısal (Excel serial) — güvenli formülle çevir
              // Sadece cell.z bir tarih formatı ise VEYA sayı Excel tarih aralığında ise
              if (isDateFormat(cell.z) || (cell.v > 20000 && cell.v < 80000)) {
                val = serialToDDMMYYYY(cell.v);
              } else {
                val = String(cell.v);
              }
            } else if (cell.t === "d") {
              // Bazı XLSX modları Date object döndürebilir — güvenli tarafta ol
              // .w (formatted text) genelde Excel'in gösterdiği metindir
              if (cell.w) val = String(cell.w).trim();
              else if (cell.v instanceof Date) {
                // LOCAL bileşenler — çünkü XLSX Date'i local time olarak oluşturur
                const d = cell.v;
                const dd = String(d.getDate()).padStart(2, "0");
                const mm = String(d.getMonth() + 1).padStart(2, "0");
                const yy = d.getFullYear();
                val = `${dd}.${mm}.${yy}`;
              }
            } else {
              // String, boolean, vs. — olduğu gibi al, backend parse etsin
              val = String(cell.w || cell.v || "").trim();
            }
          } else {
            // TARİH DEĞİL — sadece string olarak al
            // .w varsa formatted text (kullanıcının gördüğü), yoksa raw value
            val = String(cell.w !== undefined ? cell.w : (cell.v ?? "")).trim();
          }

          if (val !== "") hasAny = true;
          if (val.startsWith("↑")) isHintRow = true;
          obj[key] = val;
        }

        if (isHintRow) continue;
        if (hasAny) parsed.push(obj);
      }

      // Örnek satırı atla
      if (parsed.length > 0) {
        const first = parsed[0];
        const exampleMatch = cols.every(c =>
          !c.example || String(first[c.key] || "") === String(c.example),
        );
        if (exampleMatch && parsed.length > 1) parsed.shift();
      }

      if (parsed.length === 0) {
        setParseError("Geçerli veri satırı bulunamadı. Örnek satırı silip kendi verinizi ekleyin.");
      }
      setRows(parsed);
    } catch (ex) {
      setParseError(`Dosya okunamadı: ${ex.message}`);
    }
  };

  const doImport = async () => {
    if (rows.length === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/import/${moduleSlug}`, { items: rows });
      setResult(data);
      if (data.inserted > 0) {
        toast.success(`${data.inserted} kayıt içe aktarıldı${data.failed ? ` (${data.failed} hatalı)` : ""}`);
        if (data.failed === 0 && onImported) {
          // tamamen başarılıysa hemen kapat
          setTimeout(() => { onImported(); onClose(); }, 800);
        }
      } else {
        toast.error("Hiçbir kayıt aktarılamadı. Hataları kontrol edin.");
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const preview = rows.slice(0, 5);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto"
                     data-testid={`import-dialog-${moduleSlug}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileUp className="h-5 w-5" />
            {moduleLabel} — Toplu İçe Aktar
          </DialogTitle>
          <DialogDescription>
            Excel (.xlsx) veya CSV dosyanızı yükleyerek toplu kayıt oluşturabilirsiniz.
            Önce şablonu indirin, doldurun, sonra yükleyin.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Şablon indir */}
          <div className="surface p-4 border-dashed border-2">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-semibold text-sm mb-1">1. Şablonu İndir</div>
                <p className="text-xs text-muted-foreground">
                  Kolon başlıklarını içeren Excel şablonu. Örnek satır ve ipuçları ile birlikte.
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={downloadTemplate}
                      data-testid={`import-${moduleSlug}-tmpl-btn`}>
                <Download className="h-4 w-4 mr-2" /> Şablon
              </Button>
            </div>
          </div>

          {/* Dosya yükle */}
          <div className="surface p-4">
            <div className="font-semibold text-sm mb-2">2. Doldurulmuş Dosyayı Yükle</div>
            <input type="file" accept=".xlsx,.xls,.csv"
                   onChange={onFileChange}
                   className="block w-full text-sm text-muted-foreground
                              file:mr-4 file:py-2 file:px-4 file:rounded file:border-0
                              file:text-sm file:font-semibold
                              file:bg-primary/10 file:text-primary
                              hover:file:bg-primary/20 cursor-pointer"
                   data-testid={`import-${moduleSlug}-file`}/>
            {file && (
              <div className="mt-2 text-xs text-muted-foreground">
                Seçilen: <span className="font-mono">{file.name}</span> ({(file.size / 1024).toFixed(1)} KB)
              </div>
            )}
            {parseError && (
              <div className="mt-3 flex items-start gap-2 text-red-700 bg-red-50 dark:bg-red-950/30 border border-red-200 rounded p-2 text-xs">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{parseError}</span>
              </div>
            )}
          </div>

          {/* Önizleme */}
          {rows.length > 0 && (
            <div className="surface p-4">
              <div className="font-semibold text-sm mb-2">
                3. Önizleme ({rows.length} kayıt hazır)
              </div>
              <div className="overflow-x-auto max-h-64">
                <table className="text-xs w-full">
                  <thead className="border-b">
                    <tr>
                      {spec?.columns.map(c => (
                        <th key={c.key} className="text-left px-2 py-1 font-semibold whitespace-nowrap">
                          {c.header}{c.required && <span className="text-red-500">*</span>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((r, i) => (
                      <tr key={i} className="border-b border-muted">
                        {spec?.columns.map(c => (
                          <td key={c.key} className="px-2 py-1 whitespace-nowrap max-w-[200px] truncate">
                            {r[c.key] || <span className="text-muted-foreground">—</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {rows.length > 5 && (
                  <div className="text-xs text-muted-foreground text-center py-2 border-t">
                    ... {rows.length - 5} kayıt daha
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Sonuç */}
          {result && (
            <div className={`surface p-4 border ${
              result.failed === 0 ? "border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30"
              : "border-amber-300 bg-amber-50 dark:bg-amber-950/30"
            }`}>
              <div className="flex items-start gap-2">
                {result.failed === 0
                  ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
                  : <AlertCircle className="h-5 w-5 text-amber-600 shrink-0" />}
                <div className="flex-1">
                  <div className="font-semibold text-sm">
                    {result.inserted} / {result.total} kayıt aktarıldı
                    {result.failed > 0 && ` — ${result.failed} hata`}
                  </div>
                  {result.errors && result.errors.length > 0 && (
                    <div className="mt-2 text-xs space-y-1 max-h-40 overflow-y-auto">
                      {result.errors.map((er, i) => (
                        <div key={i} className="font-mono">
                          Satır {er.row}: <span className="text-red-700">{er.error}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            <X className="h-4 w-4 mr-2" /> Kapat
          </Button>
          {rows.length > 0 && !result && (
            <Button onClick={doImport} disabled={busy}
                    data-testid={`import-${moduleSlug}-submit`}>
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Upload className="h-4 w-4 mr-2" />
              {rows.length} Kaydı İçe Aktar
            </Button>
          )}
          {result && result.failed > 0 && result.inserted > 0 && (
            <Button onClick={() => { onImported && onImported(); onClose(); }}>
              Tamamlandı
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
